import { Stethoscope, Upload } from "lucide-react";
import { useState } from "react";

import { BulkCareGapDocumentDropzone } from "@src/features/care-protocol-opportunities/v2";
import { DocumentDropzoneTrigger } from "@src/features/documents";
import { PatientSearch } from "@src/features/patients/v2";
import { useIsPhysician } from "@src/features/physicians";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

export interface HomePageV2Props {
  className?: string;
  onFilesSelected?: (files: readonly File[]) => void;
}

export function HomePageV2({ className, onFilesSelected }: HomePageV2Props) {
  const canCreatePatient = useIsPhysician();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  return (
    <BulkCareGapDocumentDropzone
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col px-6 py-10 md-stitch:px-10",
        className,
      )}
      contentClassName="flex min-h-0 flex-1 flex-col items-center justify-center"
      onFilesSelected={onFilesSelected}
      onStatusChange={setUploadStatus}
    >
      <main className="flex w-full max-w-[520px] flex-col items-center">
        <div className="mb-[18px] flex size-12 items-center justify-center rounded-[13px] bg-primary/10 text-primary">
          <Stethoscope className="size-[23px]" aria-hidden="true" />
        </div>

        <h1 className="sr-only">Home</h1>
        <p className="mb-4 max-w-[360px] text-center text-base font-semibold leading-6 text-foreground text-balance">
          Search for a patient, or drop care gap files to get started.
        </p>

        <PatientSearch
          open={isSearchOpen}
          onOpenChange={setIsSearchOpen}
          canCreatePatient={canCreatePatient}
          label="Search patients from home"
          placeholder="Search a patient or drop a care gap file"
          navigateStateSource="v2 home search"
          className="mx-auto w-full max-w-[440px]"
          contentClassName="max-h-[260px] overflow-y-auto"
          trailingAction={
            <DocumentDropzoneTrigger ariaLabel="Upload care gap files">
              {({ isUploading, triggerProps }) => (
                <Button
                  {...triggerProps}
                  variant="ghost"
                  className="size-8 rounded-[8px] border border-border bg-surface p-0 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-45"
                >
                  <Upload className="size-4" aria-hidden="true" />
                  <span className="sr-only">
                    {isUploading
                      ? "Uploading care gap files"
                      : "Upload care gap files"}
                  </span>
                </Button>
              )}
            </DocumentDropzoneTrigger>
          }
        />

        <div
          className="mt-3 flex items-center justify-center gap-1.5 text-center font-mono text-[11px] text-muted-foreground"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {uploadStatus ?? "Drag & drop a file, or use the upload button"}
        </div>
      </main>
    </BulkCareGapDocumentDropzone>
  );
}
